import axios from 'axios';
import React from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { serialize } from 'object-to-formdata';

import { useAlert } from '../../context/AlertProvider';

import MyHead from '@/components/MyHead';
import Tabs from '@/components/v2/Pages/Contacts/Tabs';
import Input from '@/components/v2/Forms/Input';
import Checkbox from '@/components/v2/Forms/Checkbox';
import { Button } from '@/components/v2/UI/Button';
import TextArea from '@/components/v2/Forms/TextArea';
import SupportLinks from '@/components/v2/Pages/Contacts/SupportLinks';
import get from 'lodash/get';

import { getToken } from '@/helpers/auth';

const Contacts: React.FC = () => {
  const token = getToken();
  const alert = useAlert();
  const { t } = useTranslation();

  const formik = useFormik({
    validateOnChange: false,
    initialValues: {
      fullname: '',
      email: '',
      subject: '',
      message: '',
      file: '',
      privacy: false
    },
    validationSchema: Yup.object().shape({
      fullname: Yup.string().required(t('REQUIRED_FIELD')),
      email: Yup.string()
        .matches(
          /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,10}$/i,
          t('INVALID_EMAIL')
        )
        .required(t('REQUIRED_FIELD')),
      subject: Yup.string().required(t('REQUIRED_FIELD')),
      message: Yup.string().required(t('REQUIRED_FIELD')),
      privacy: Yup.boolean().oneOf([true]).required()
    }),
    async onSubmit(values) {
      try {
        await axios.post(
          '/api/sendgrid',
          serialize({ ...values, formName: 'private' })
        );

        formik.resetForm();
        alert.show(t('SEND_FORM_SUCCESS'));
      } catch (error) {
        alert.error(t('GENERIC_ERROR'));
      }
    }
  });

  return (
    <>
      <MyHead />

      <Tabs />

      <div className="section">
        <div className="grid gap-y-12 grid-cols-12 md:gap-12 xl:gap-20">
          <div className="col-span-12 space-y-6 md:col-span-6">
            <h3 className="text-body">{t('P_CONTACTS.GENERAL.FORM.TITLE')}</h3>

            <form
              onSubmit={formik.handleSubmit}
              className="space-y-3 md:space-y-6"
            >
              <Input
                type="text"
                name="fullname"
                placeholder={t('NAME')}
                className="bg-transparent focus:ring-gray-400 focus:border-gray-400"
                value={formik.values.fullname}
                onChange={formik.handleChange}
                error={formik.errors.fullname}
              />

              <Input
                type="email"
                name="email"
                placeholder={t('MAIL_ADDRESS')}
                className="bg-transparent focus:ring-gray-400 focus:border-gray-400"
                value={formik.values.email}
                onChange={formik.handleChange}
                error={formik.errors.email}
              />

              <Input
                type="text"
                name="subject"
                placeholder={t('SUBJECT')}
                className="bg-transparent focus:ring-gray-400 focus:border-gray-400"
                value={formik.values.subject}
                onChange={formik.handleChange}
                error={formik.errors.subject}
              />

              <TextArea
                name="message"
                placeholder={t('MESSAGE')}
                className="bg-transparent focus:ring-gray-400 focus:border-gray-400"
                value={formik.values.message}
                onChange={formik.handleChange}
                error={formik.errors.message}
              />

              <Input
                type="file"
                label={t('P_REPAIR.FORM.DEFECT_IMAGE')}
                accept="image/*,application/pdf"
                onChange={({ currentTarget }) => {
                  formik.setFieldValue('file', get(currentTarget, 'files.0'));
                }}
                error={formik.errors.file}
              />

              <Checkbox
                name="privacy"
                required
                checked={formik.values.privacy}
                onChange={formik.handleChange}
                error={formik.errors.privacy}
              >
                {t('MENU.NEWSLETTER.PRIVACY')}
                <a
                  href={`https://www.iubenda.com/privacy-policy/${t(
                    'IUBENDA.POLICY_ID'
                  )}`}
                  className="iubenda-white no-brand iubenda-noiframe iub-body-embed text-primary"
                  title="Privacy Policy"
                  target="_blank"
                  rel="noreferrer"
                >
                  {t('MENU.PRIVACY_POLICY')}
                </a>
              </Checkbox>

              <Button
                type="submit"
                variant="dark"
                className="w-full"
                loading={formik.isSubmitting}
              >
                {t('SEND')}
              </Button>
            </form>
          </div>

          <SupportLinks />
        </div>
      </div>
    </>
  );
};

export default Contacts;
